import { PaginationRequest } from "gitpod-next-api/gitpod/v1/pagination_pb";

import type { PaginationResponse } from "gitpod-next-api/gitpod/v1/pagination_pb";

/**
 * Auto-pagination utility that handles pagination requests internally.
 * @param fn Function that accepts no parameters but returns a paginated response
 * @param pageSize Optional page size (defaults to API default, max 100)
 * @returns All aggregated results from paginated requests
 *
 * @example
 * // Example usage:
 * const allUsers = await autoPaginateRaw(
 *   (params) => api.userService.listUsers(params),
 *   { organizationId: "my-org-id" }
 * ).then((results) => results.flatMap((r) => r.users));
 */
export async function autoPaginateRaw<T extends { pagination?: PaginationResponse }, P>(
    fn: (params: P) => Promise<T>,
    baseParams: P,
    pageSize?: number,
) {
    const maxRequests = 100;
    const results: T[] = [];

    let token = "";
    for (let i = 0; i < maxRequests; i++) {
        const paramsWithPagination = {
            ...baseParams,
            pagination: new PaginationRequest({ token, ...(pageSize !== undefined && { pageSize }) }),
        };

        const result = await fn(paramsWithPagination);
        results.push(result);
        token = result.pagination?.nextToken || "";

        if (i === maxRequests - 1 && !!token) {
            console.error(`Cancelling pagination at ${maxRequests} requests.`);
        }
        if (!token) {
            break;
        }
    }

    return results;
}

/**
 * Specialized paginator for collections that automatically extracts and flattens items.
 * @param fn Function that accepts parameters and returns a paginated response
 * @param baseParams Base parameters to pass to the function (without pagination)
 * @param extractor Function to extract the collection from each response
 * @param pageSize Optional page size (defaults to API default, max 100)
 * @returns Flattened array of all items across all pages
 *
 * @example
 * // Example usage:
 * const allUsers = await autoPaginate(
 *   (params) => api.userService.listUsers(params),
 *   { organizationId: "my-org-id" },
 *   (response) => response.users
 * );
 */
export async function autoPaginate<T extends { pagination?: PaginationResponse }, P, R>(
    fn: (params: P) => Promise<T>,
    baseParams: P,
    extractor: (result: T) => R[],
    pageSize?: number,
): Promise<R[]> {
    const results = await autoPaginateRaw(fn, baseParams, pageSize);
    return results.flatMap(extractor);
}
